import { Image } from 'expo-image';
import { VStack, Box } from 'native-base';

// Redux
import { useGetCurrentUserQuery } from '@breakroom-nx/redux';

// Library Imports
import { useState, useEffect } from 'react';

// Components
import {
  PrimaryButton,
  TertiaryButton,
  PointsTag,
  AnimatedShuffleView,
} from '../../';

import { Shuffler, shuffle } from '@breakroom-nx/classes';

import { ImageURISource, Platform } from 'react-native';
import * as Picker from 'expo-image-picker';
import { useActionSheet } from '@expo/react-native-action-sheet';

export interface ModifyProfilePhotoProps {
  onPhotoSelected: (image: string) => void;
  showPoints?: boolean;
}

export function ModifyProfilePhoto({
  onPhotoSelected,
  showPoints = false,
}: ModifyProfilePhotoProps) {
  const { data: currentUser } = useGetCurrentUserQuery();
  const [shuffler, setShuffler] = useState<Shuffler>();
  const [showAnonymousAvatar, setShowAnonymousAvatar] = useState(
    currentUser?.avatar ? false : true
  );
  const [image, setImage] = useState<string | undefined>(currentUser?.avatar);

  const { showActionSheetWithOptions } = useActionSheet();
  const showImageOptions = (selectedIndex?: number) => {
    switch (selectedIndex) {
      case 0:
        pickImage();
        break;
      case 1:
        setImage(undefined);
        setShowAnonymousAvatar(true);
        break;
      default:
        return;
    }
  };

  const pickImage = async () => {
    const result = await Picker.launchImageLibraryAsync({
      mediaTypes: Picker.MediaTypeOptions.All,
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.1,
    });

    if (!result.cancelled) {
      const imageURI =
        Platform.OS === 'ios' ? result.uri.replace('file://', '') : result.uri;
      setImage(imageURI);
      onPhotoSelected(imageURI);
    } else {
      setImage(currentUser?.avatar);
    }
  };

  useEffect(() => {
    setShuffler(shuffle());
  }, []);

  useEffect(() => {
    if (!showAnonymousAvatar && !image) {
      const timeout = setTimeout(() => {
        setShuffler(shuffle());
        setShowAnonymousAvatar(true);
      }, 500);
      return () => clearTimeout(timeout);
    } else {
      return;
    }
  }, [image, showAnonymousAvatar]);

  return (
    <VStack alignItems={'center'} space={8}>
      <Box alignItems={'center'}>
        {image ? (
          <Box
            size={100}
            borderRadius={'full'}
            bgColor={currentUser?.theme}
            overflow={'hidden'}
          >
            <Image
              source={{ uri: image }}
              cachePolicy={'memory'}
              accessibilityLabel={
                currentUser ? `${currentUser.username}'s avatar` : 'avatar'
              }
              contentFit={'cover'}
              key={image}
              style={{
                height: '100%',
              }}
            />
          </Box>
        ) : (
          shuffler && (
            <AnimatedShuffleView
              visible={showAnonymousAvatar}
              color={shuffler?.color}
              avatar={shuffler?.object.avatar}
            />
          )
        )}
      </Box>

      <VStack space={3}>
        <PrimaryButton
          disabled={false}
          text={image ? 'Edit your photo' : 'Add your own'}
          bg={shuffler?.color}
          endIcon={
            showPoints ? <PointsTag amount={30} payment={false} /> : null
          }
          onPress={() => {
            if (image === undefined) {
              pickImage();
            } else {
              showActionSheetWithOptions(
                {
                  title: 'Edit Photo',
                  options: ['Change Photo', 'Delete Photo', 'Cancel'],
                  destructiveButtonIndex: 1,
                  cancelButtonIndex: 2,
                },
                (i) => {
                  showImageOptions(i);
                }
              );
            }
          }}
        />

        {!image ? (
          <TertiaryButton
            text={'Shuffle again!'}
            color={shuffler?.color}
            onPress={() => {
              setShowAnonymousAvatar(false);
            }}
          />
        ) : null}
      </VStack>
    </VStack>
  );
}
